import AddressInput from 'components/forms/inputs/rhf-inputs/AddressInput';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { isValidIban, isValidSwift } from 'components/forms/inputs/rhf-validators/validators';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { CompanyIbanDto } from 'types/companies';
import { AddressDto } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';

interface Props {
    titleId: string;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
    onSubmit: (value: CompanyIbanDto) => void;
    initialData: CompanyIbanForm;
    companyIban?: CompanyIbanDto;
}

interface CompanyIbanForm {
    id: number | null;
    iban: string | null;
    currencyType: { label: string; value: string } | null;
    ibanReceiverName: string | null;
    bankName: string | null;
    bankCode: string | null;
    bankSwift: string | null;
    bankAddress: AddressDto | null;
    mainIban: boolean;
}

const CompanyIbanEditModal = (props: Props) => {
    const { titleId, onClose, classifiers, onSubmit, initialData, companyIban } = props;

    const currencyOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    const { handleSubmit, control, setValue, getValues } = useForm<CompanyIbanForm>({
        defaultValues: initialData,
        values: initialData,
    });

    const onSave = async () => {
        await handleSubmit((data) => onSubmit(mapFormDataToDto(data)))();
    };

    const validIban = useCallback((val: unknown) => isValidIban(val, t('validation.iban-must-be-valid')), []);
    const validSwift = useCallback((val: unknown) => isValidSwift(val, t('validation.swift-must-be-valid')), []);

    return (
        <>
            <ModalHeader title={t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-[23px] pt-[15px] pb-[45px]">
                <div className="flex flex-col gap-4">
                    <TextInput
                        control={control}
                        label="financial-conditions.company.iban"
                        name="iban"
                        required
                        validate={validIban}
                    />
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <TextInput
                                control={control}
                                label="financial-conditions.company.bank-swift"
                                name="bankSwift"
                                required
                                validate={validSwift}
                            />
                        </div>
                        <div className="flex-1">
                            <SelectInput
                                setValue={setValue}
                                control={control}
                                label="currency"
                                required
                                name="currencyType"
                                options={currencyOptions}
                                getOptionLabel="label"
                                getOptionIdentifier="value"
                                placeholder="forms.select-currency"
                            />
                        </div>
                    </div>
                    <TextInput
                        control={control}
                        label="financial-conditions.company.iban-receiver-name"
                        name="ibanReceiverName"
                        required
                    />
                    <TextInput
                        control={control}
                        label="financial-conditions.company.bank-name"
                        name="bankName"
                        required
                    />
                    <TextInput control={control} label="financial-conditions.company.bank-code" name="bankCode" />
                    <CheckboxInput
                        control={control}
                        name="mainIban"
                        label="financial-conditions.main-account"
                        readonly={getValues('mainIban') ?? false}
                    />
                    <AddressInput
                        label="financial-conditions.company.bank-address"
                        name="bankAddress"
                        control={control}
                        classifiers={classifiers}
                        customFields={companyIban?.bankAddress?.customFields as CustomField[]}
                        required
                    />
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
            />
        </>
    );
};

export default CompanyIbanEditModal;

const mapFormDataToDto = (form: CompanyIbanForm): CompanyIbanDto => ({
    id: form.id || null,
    iban: form.iban || null,
    currencyTypeCode: form?.currencyType?.value || null,
    ibanReceiverName: form.ibanReceiverName || null,
    bankName: form.bankName || null,
    bankCode: form.bankCode || null,
    bankSwift: form.bankSwift || null,
    bankAddress: form.bankAddress || null,
    mainIban: form.mainIban || false,
});
